import { useEffect, useRef, useCallback } from 'react';
import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { Device } from '@capacitor/device';
import { Badge } from '@capawesome/capacitor-badge';
import { FirebaseMessaging } from '@capacitor-firebase/messaging';
import { useNavigate } from 'react-router-dom';
import apiClient from '../utils/apiClient';

/**
 * Hook that manages push notification registration and handling.
 * Only runs on native platforms (iOS/Android via Capacitor).
 *
 * - Requests permission & gets FCM token on mount (when user is logged in)
 * - Sends device token to backend on registration
 * - Handles notification taps for deep linking
 * - Removes token from backend on logout
 */
export function usePushNotifications(user) {
  const navigate = useNavigate();
  const tokenRef = useRef(null);
  const listenersRegistered = useRef(false);

  const registerToken = useCallback(async (tokenValue) => {
    try {
      // Device model/OS ride along for a rough hardware census. Older app
      // shells lack the Device plugin, so never let it block registration.
      let deviceModel;
      let osVersion;
      if (Capacitor.isPluginAvailable('Device')) {
        try {
          const info = await Device.getInfo();
          deviceModel = info.model;
          osVersion = info.osVersion;
        } catch {
          // census is best-effort
        }
      }
      await apiClient.post('/notifications/register', {
        token: tokenValue,
        platform: Capacitor.getPlatform(), // 'ios' or 'android'
        deviceModel,
        osVersion,
      });
      tokenRef.current = tokenValue;
    } catch (error) {
      console.error('[Push] Failed to register token with backend:', error);
    }
  }, []);

  const unregisterToken = useCallback(async () => {
    if (!tokenRef.current) return;
    try {
      await apiClient.delete('/notifications/unregister', {
        data: { token: tokenRef.current },
      });
      tokenRef.current = null;
    } catch (error) {
      console.error('[Push] Failed to unregister token:', error);
    }
  }, []);

  useEffect(() => {
    // Only run on native platforms
    console.log('[Push] Hook running, isNative:', Capacitor.isNativePlatform(), 'platform:', Capacitor.getPlatform(), 'user:', !!user);
    if (!Capacitor.isNativePlatform()) return;
    if (!user) return;

    const setup = async () => {
      console.log('[Push] Starting setup...');
      // Check current permission status
      let permStatus = await FirebaseMessaging.checkPermissions();
      console.log('[Push] Permission status:', permStatus.receive);

      if (permStatus.receive === 'prompt') {
        permStatus = await FirebaseMessaging.requestPermissions();
      }

      if (permStatus.receive !== 'granted') {
        console.log('[Push] Permission not granted');
        return;
      }

      // Clear badge and delivered notifications on app open
      FirebaseMessaging.removeAllDeliveredNotifications();
      Badge.clear();

      // Register listeners only once
      if (!listenersRegistered.current) {
        listenersRegistered.current = true;

        // Clear badge when app resumes from background
        App.addListener('appStateChange', ({ isActive }) => {
          if (isActive) {
            FirebaseMessaging.removeAllDeliveredNotifications();
            Badge.clear();
          }
        });

        // Called when a notification is received while app is in foreground
        FirebaseMessaging.addListener('notificationReceived', (event) => {
          console.log('[Push] Foreground notification:', event.notification?.title);
          // Could show an in-app toast here if desired
        });

        // Called when user taps a notification
        FirebaseMessaging.addListener('notificationActionPerformed', (event) => {
          const data = event.notification?.data;
          if (data?.route) {
            navigate(data.route);
          }
        });
      }

      // Get FCM token directly (replaces register() + registration listener)
      try {
        const { token } = await FirebaseMessaging.getToken();
        console.log('[Push] FCM token:', token?.substring(0, 20) + '...');
        await registerToken(token);
      } catch (error) {
        console.error('[Push] Failed to get FCM token:', error);
      }
    };

    setup().catch(console.error);
  }, [user, navigate, registerToken]);

  // Return unregister for logout use
  return { unregisterToken };
}
